import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api, formatKES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import PaymentDialog from "../components/PaymentDialog";
import { Link } from "react-router-dom";
import { Crown, Wallet, Briefcase, MessageSquare, TicketCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import InlineChat from "../components/InlineChat";

export default function CustomerDashboard() {
  const { user, refresh } = useAuth();
  const [hires, setHires] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [chosenTier, setChosenTier] = useState(null);

  const load = async () => {
    const [h, t, p, tr] = await Promise.all([
      api.get("/hires"),
      api.get("/tickets"),
      api.get("/payments/me"),
      api.get("/tiers", { params: { audience: "customer" } }),
    ]);
    setHires(h.data);
    setTickets(t.data);
    setPayments(p.data);
    setTiers(tr.data);
  };

  useEffect(() => { load(); }, []);

  const completeHire = async (id) => {
    await api.patch(`/hires/${id}`, { status: "completed" });
    toast.success("Marked complete");
    load();
  };

  const runPayroll = async (h) => {
    const amount = Number(prompt(`Salary amount (KES) for ${h.employee_name}?`, h.offered_rate));
    if (!amount) return;
    const period = prompt("Payroll period (e.g. Feb 2026)?", new Date().toLocaleString("en-KE", { month: "short", year: "numeric" }));
    if (!period) return;
    try {
      const { data } = await api.post("/payroll", { employee_id: h.employee_id, amount, period, note: "Salary" });
      toast.success(`Paid ${formatKES(data.amount_net)} (commission ${formatKES(data.commission)})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FE]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-2">Employer dashboard</div>
            <h1 className="font-display text-4xl font-bold tracking-tight">Welcome, {user?.full_name?.split(" ")[0]}</h1>
            <div className="mt-2 flex items-center gap-3">
              <Badge data-testid="customer-tier-badge" className="bg-[#1E3A8A] hover:bg-[#1E3A8A] text-[#F8F9FE] capitalize">{user?.tier} Tier</Badge>
              <span className="text-sm text-[#5B6478]">{user?.subscription_status === "active" ? "Subscription active" : "No active subscription"}</span>
            </div>
          </div>
          <Button asChild className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white">
            <Link to="/browse" data-testid="dash-browse">Find staff</Link>
          </Button>
        </div>

        <div className="grid lg:grid-cols-4 gap-4 mt-10">
          <StatCard label="Active hires" value={hires.filter((h) => h.status === "accepted" || h.status === "active").length} icon={Briefcase} />
          <StatCard label="Pending requests" value={hires.filter((h) => h.status === "pending").length} icon={TicketCheck} />
          <StatCard label="Open tickets" value={tickets.filter((t) => t.status !== "resolved").length} icon={MessageSquare} />
          <StatCard label="Spent (KES)" value={formatKES(payments.filter((p) => p.status === "completed").reduce((s, p) => s + p.amount, 0))} icon={Wallet} />
        </div>

        <Tabs defaultValue="hires" className="mt-10">
          <TabsList className="bg-[#EEF0FA] h-11">
            <TabsTrigger value="hires" data-testid="tab-hires">My Placements</TabsTrigger>
            <TabsTrigger value="tickets" data-testid="tab-tickets">Tickets</TabsTrigger>
            <TabsTrigger value="subscription" data-testid="tab-subscription">Subscription</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="hires" className="mt-6">
            <div className="bg-white border border-[#E4E7F5] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#EEF0FA] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Staff</th>
                    <th className="px-4 py-3 font-semibold">Category</th>
                    <th className="px-4 py-3 font-semibold">Start</th>
                    <th className="px-4 py-3 font-semibold">Rate</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hires.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#5B6478]">No placements yet</td></tr>}
                  {hires.map((h) => (
                    <tr key={h.id} className="border-t border-[#E4E7F5]" data-testid={`hire-row-${h.id}`}>
                      <td className="px-4 py-3 font-semibold">{h.employee_name}</td>
                      <td className="px-4 py-3 capitalize">{h.category.replace("-", " ")}</td>
                      <td className="px-4 py-3">{h.start_date}</td>
                      <td className="px-4 py-3">{formatKES(h.offered_rate)}</td>
                      <td className="px-4 py-3"><StatusBadge status={h.status} /></td>
                      <td className="px-4 py-3 flex gap-2">
                        {h.status === "accepted" && (
                          <>
                            <Button size="sm" data-testid={`payroll-${h.id}`} onClick={() => runPayroll(h)} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white">Run Payroll</Button>
                            <Button size="sm" variant="outline" data-testid={`complete-${h.id}`} onClick={() => completeHire(h.id)} className="border-[#E4E7F5]">Complete</Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="tickets" className="mt-6 space-y-3">
            {tickets.length === 0 && <div className="text-center py-10 text-[#5B6478]">No tickets yet</div>}
            {tickets.map((t) => (
              <div key={t.id} className="bg-white border border-[#E4E7F5] rounded-md p-5">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-display font-semibold">{t.subject}</div>
                    <div className="text-sm text-[#5B6478] mt-1">{t.message}</div>
                  </div>
                  <StatusBadge status={t.status} />
                </div>
                {t.admin_response && (
                  <div className="mt-3 bg-[#EEF0FA] rounded-md p-3 text-sm">
                    <span className="label-eyebrow">KSB Response:</span> {t.admin_response}
                  </div>
                )}
                <InlineChat
                  messages={t.messages || []}
                  currentUserId={user?.id}
                  locked={t.status === "resolved"}
                  testIdPrefix={`ticket-chat-${t.id}`}
                  onSend={async (body) => {
                    try {
                      await api.post(`/tickets/${t.id}/messages`, { body });
                      load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                />
              </div>
            ))}
          </TabsContent>

          <TabsContent value="subscription" className="mt-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {tiers.map((t) => {
                const isCurrent = user?.current_tier_id === t.id;
                return (
                  <div key={t.id} data-testid={`tier-tile-${t.id}`} className={`price-tile ${isCurrent ? "is-current" : ""}`}>
                    {isCurrent && <span className="price-tile-ribbon">Current</span>}
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold text-xl">{t.name}</div>
                      {isCurrent && <Crown className="w-5 h-5 text-[#7C3AED]" />}
                    </div>
                    <div className="mt-2 font-display text-3xl font-extrabold text-[#1E3A8A]">
                      {formatKES(t.price)}<span className="text-xs text-[#5B6478] font-normal">/{t.period === "monthly" ? "mo" : "once"}</span>
                    </div>
                    <ul className="mt-5 space-y-2 text-sm">
                      {t.features.map((f, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#1E3A8A] mt-0.5 shrink-0" /> {f}</li>)}
                    </ul>
                    <Button
                      data-testid={`subscribe-${t.id}`}
                      disabled={isCurrent}
                      onClick={() => { setChosenTier(t); setPayOpen(true); }}
                      className="w-full mt-6 bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]"
                    >
                      {isCurrent ? "Current plan" : "Subscribe"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="payments" className="mt-6">
            <div className="bg-white border border-[#E4E7F5] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#EEF0FA] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Purpose</th>
                    <th className="px-4 py-3 font-semibold">Amount</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#5B6478]">No payments yet</td></tr>}
                  {payments.map((p) => (
                    <tr key={p.id} className="border-t border-[#E4E7F5]">
                      <td className="px-4 py-3">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 capitalize">{p.purpose}</td>
                      <td className="px-4 py-3">{formatKES(p.amount)}</td>
                      <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3 font-mono text-xs">{p.mpesa_receipt || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {chosenTier && (
        <PaymentDialog
          open={payOpen}
          onOpenChange={setPayOpen}
          amount={chosenTier.price}
          purpose="subscription"
          tierId={chosenTier.id}
          defaultPhone={user?.phone}
          title={`Subscribe to ${chosenTier.name}`}
          description={`${chosenTier.period === "monthly" ? "Monthly subscription" : "One-time fee"} via M-Pesa STK Push`}
          onSuccess={async () => { await refresh(); load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[#E4E7F5] rounded-md p-5 flex items-center justify-between">
      <div>
        <div className="label-eyebrow">{label}</div>
        <div className="font-display text-2xl font-bold mt-1">{value}</div>
      </div>
      <Icon className="w-6 h-6 text-[#1E3A8A]" />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    accepted: "bg-[#1E3A8A] text-[#F8F9FE]",
    active: "bg-[#1E3A8A] text-[#F8F9FE]",
    completed: "bg-gray-200 text-gray-700",
    rejected: "bg-[#B91C1C] text-white",
    open: "bg-amber-100 text-amber-800",
    in_progress: "bg-blue-100 text-blue-800",
    resolved: "bg-[#1E3A8A] text-[#F8F9FE]",
    failed: "bg-[#B91C1C] text-white",
  };
  return <span className={`px-2 py-1 rounded-md text-xs font-semibold capitalize ${map[status] || "bg-gray-100"}`}>{status?.replace("_", " ")}</span>;
}
